/**
 * TranscriptionAdmin — admin dashboard for AI Transcription.
 * Features: stats, feature flags, user permissions, job queue.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const API = '/api/ai';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

interface Stats {
  total_recordings: number;
  transcribed_calls: number;
  pending_in_queue: number;
  processing: number;
  failed: number;
  avg_processing_seconds: number;
  top_keywords: { keyword: string; count: number }[];
  sentiment_distribution: Record<string, number>;
  daily_activity: { date: string; count: number }[];
}

interface Features {
  transcription_enabled: boolean;
  summary_enabled: boolean;
  sentiment_enabled: boolean;
  action_items_enabled: boolean;
  whisper_model: string;
  llm_model: string;
  language: string;
}

interface UserPerm {
  id: number;
  user_id: number;
  username: string;
  display_name: string;
  role: string;
  can_use_ai: boolean;
  can_view_transcript: boolean;
  can_download_transcript: boolean;
  can_view_summary: boolean;
  can_view_sentiment: boolean;
  can_view_action_items: boolean;
}

interface Job {
  id: number;
  recording_id: number;
  status: string;
  filename: string;
  original_name?: string;
  attempts: number;
  error_message: string;
  processing_time_ms: number;
  created_at: string;
  completed_at: string;
}

const SENTIMENT_COLORS: Record<string, string> = {
  positive: '#10b981',
  negative: '#ef4444',
  neutral: '#64748b',
  mixed: '#f59e0b',
};

const STATUS_COLORS: Record<string, string> = {
  pending: '#f59e0b',
  processing: '#6366f1',
  completed: '#10b981',
  failed: '#ef4444',
  skipped: '#64748b',
};

const WHISPER_MODELS = ['tiny', 'base', 'small', 'medium'];
const LLM_MODELS = ['qwen2.5:3b', 'qwen2.5:7b', 'llama3.2:3b', 'mistral:7b', 'gemma2:2b'];
const LANGUAGES = ['auto', 'en', 'ar', 'tr', 'fr', 'de', 'es', 'it', 'ru', 'zh'];

export default function TranscriptionAdmin({ user }: { user: any }) {
  const [tab, setTab] = useState<'overview' | 'features' | 'permissions' | 'jobs'>('overview');
  const [stats, setStats] = useState<Stats | null>(null);
  const [features, setFeatures] = useState<Features | null>(null);
  const [users, setUsers] = useState<UserPerm[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [jobsTotal, setJobsTotal] = useState(0);
  const [jobPage, setJobPage] = useState(1);
  const [jobFilter, setJobFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingPerms, setSavingPerms] = useState<Record<number, boolean>>({});
  const [toast, setToast] = useState('');

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const loadStats = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/transcription/admin/stats`, auth());
      setStats(r.data);
    } catch {}
  }, []);

  const loadFeatures = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/transcription/features`, auth());
      setFeatures(r.data);
    } catch {}
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/transcription/users`, auth());
      setUsers(r.data);
    } catch {}
  }, []);

  const loadJobs = useCallback(async (pg = 1, status = '') => {
    try {
      const params: any = { page: pg, page_size: 20 };
      if (status) params.status = status;
      const r = await axios.get(`${API}/transcription/jobs`, { params, ...auth() });
      setJobs(r.data.items || []);
      setJobsTotal(r.data.total || 0);
    } catch {}
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadStats(), loadFeatures(), loadUsers()]).finally(() => setLoading(false));
  }, [loadStats, loadFeatures, loadUsers]);

  useEffect(() => {
    if (tab === 'jobs') loadJobs(jobPage, jobFilter);
  }, [tab, jobPage, jobFilter, loadJobs]);

  const saveFeatures = async () => {
    if (!features) return;
    setSaving(true);
    try {
      await axios.put(`${API}/transcription/features`, features, auth());
      showToast('✅ Features saved successfully');
    } catch (e: any) {
      showToast('❌ ' + (e?.response?.data?.detail || 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const updateUserPerm = async (userId: number, field: string, value: boolean) => {
    setSavingPerms(p => ({ ...p, [userId]: true }));
    try {
      await axios.put(`${API}/transcription/permissions/${userId}`, { [field]: value }, auth());
      setUsers(u => u.map(u => u.user_id === userId ? { ...u, [field]: value } : u));
    } catch (e: any) {
      showToast('❌ ' + (e?.response?.data?.detail || 'Update failed'));
    } finally {
      setSavingPerms(p => ({ ...p, [userId]: false }));
    }
  };

  const reprocess = async (recordingId: number) => {
    try {
      await axios.post(`${API}/transcripts/reprocess`, { recording_id: recordingId }, auth());
      showToast('✅ Re-queued for processing');
      loadJobs(jobPage, jobFilter);
    } catch (e: any) {
      showToast('❌ ' + (e?.response?.data?.detail || 'Reprocess failed'));
    }
  };

  if (loading) return (
    <div style={cont}>
      <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>⏳ Loading…</div>
    </div>
  );

  return (
    <div style={cont}>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          background: toast.startsWith('❌') ? '#ef444422' : '#10b98122',
          border: `1px solid ${toast.startsWith('❌') ? '#ef4444' : '#10b981'}66`,
          borderRadius: 10, padding: '12px 20px', color: '#e2e8f0', fontSize: 14,
          boxShadow: '0 8px 30px rgba(0,0,0,0.4)',
        }}>
          {toast}
        </div>
      )}

      {/* Page header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#f1f5f9' }}>🎙 AI Transcription</h2>
        <p style={{ margin: '6px 0 0', fontSize: 13, color: '#64748b' }}>
          Manage call transcription, AI analysis, and user permissions.
        </p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #1e293b', marginBottom: 24, gap: 4, flexWrap: 'wrap' }}>
        {[
          ['overview', '📊 Overview'],
          ['features', '⚙ Features'],
          ['permissions', '🔐 Permissions'],
          ['jobs', '📋 Job Queue'],
        ].map(([t, label]) => (
          <button key={t} onClick={() => setTab(t as any)} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: '10px 16px', fontSize: 13, fontWeight: tab === t ? 700 : 400,
            color: tab === t ? '#c4b5fd' : '#64748b',
            borderBottom: tab === t ? '2px solid #8b5cf6' : '2px solid transparent',
          }}>
            {label}
          </button>
        ))}
      </div>

      {/* OVERVIEW */}
      {tab === 'overview' && stats && (
        <div>
          {/* Stat cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14, marginBottom: 24 }}>
            {[
              { label: 'Total Recordings', value: stats.total_recordings, icon: '🎵', color: '#6366f1' },
              { label: 'Transcribed', value: stats.transcribed_calls, icon: '📝', color: '#10b981' },
              { label: 'In Queue', value: stats.pending_in_queue, icon: '⏳', color: '#f59e0b' },
              { label: 'Processing', value: stats.processing, icon: '⚙', color: '#6366f1' },
              { label: 'Failed', value: stats.failed, icon: '❌', color: '#ef4444' },
              { label: 'Avg Time (s)', value: stats.avg_processing_seconds, icon: '⏱', color: '#06b6d4' },
            ].map(s => (
              <div key={s.label} style={{ background: '#1e293b', borderRadius: 12, padding: '18px 16px', textAlign: 'center' }}>
                <div style={{ fontSize: 28, marginBottom: 6 }}>{s.icon}</div>
                <div style={{ fontSize: 26, fontWeight: 800, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>{s.label}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {/* Sentiment Distribution */}
            {Object.keys(stats.sentiment_distribution).length > 0 && (
              <div style={card}>
                <div style={cardHdr}>Sentiment Distribution</div>
                {Object.entries(stats.sentiment_distribution).map(([s, c]) => (
                  <div key={s} style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 12, color: '#94a3b8', textTransform: 'capitalize' }}>{s}</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: SENTIMENT_COLORS[s] || '#94a3b8' }}>{c}</span>
                    </div>
                    <div style={{ height: 6, background: '#0f172a', borderRadius: 3 }}>
                      <div style={{
                        height: '100%', borderRadius: 3,
                        background: SENTIMENT_COLORS[s] || '#64748b',
                        width: `${(c / (stats.transcribed_calls || 1)) * 100}%`,
                      }} />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Top Keywords */}
            {stats.top_keywords.length > 0 && (
              <div style={card}>
                <div style={cardHdr}>Top Keywords (30 days)</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                  {stats.top_keywords.map(kw => (
                    <span key={kw.keyword} style={{
                      background: '#0f172a', border: '1px solid #334155',
                      borderRadius: 20, padding: '4px 10px', fontSize: 12,
                      color: '#94a3b8',
                    }}>
                      {kw.keyword} <span style={{ color: '#6366f1', fontWeight: 700 }}>{kw.count}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Daily activity */}
          {stats.daily_activity.length > 0 && (
            <div style={{ ...card, marginTop: 16 }}>
              <div style={cardHdr}>Daily Activity (7 days)</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', height: 80, marginTop: 12 }}>
                {stats.daily_activity.map(d => {
                  const maxVal = Math.max(...stats.daily_activity.map(x => x.count), 1);
                  const pct = (d.count / maxVal) * 100;
                  return (
                    <div key={d.date} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                      <div style={{ fontSize: 10, color: '#6366f1', fontWeight: 700 }}>{d.count}</div>
                      <div style={{ width: '100%', background: '#6366f1', borderRadius: '3px 3px 0 0', height: `${Math.max(pct, 5)}%` }} />
                      <div style={{ fontSize: 9, color: '#64748b' }}>{d.date.slice(5)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* FEATURES */}
      {tab === 'features' && features && (
        <div>
          <div style={card}>
            <div style={cardHdr}>AI Feature Flags</div>
            <p style={{ fontSize: 12, color: '#64748b', marginTop: 0, marginBottom: 16 }}>
              Only Super Admin and Tenant Admin can enable these features.
              Changes apply to all new recordings for this tenant.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[
                ['transcription_enabled', 'Enable AI Transcription', 'Convert call recordings to text using Faster-Whisper'],
                ['summary_enabled', 'Enable AI Summary', 'Generate executive and detailed call summaries using LLM'],
                ['sentiment_enabled', 'Enable Sentiment Analysis', 'Analyze call sentiment (positive/negative/neutral/mixed)'],
                ['action_items_enabled', 'Enable Action Items', 'Extract follow-up tasks and action items from calls'],
              ].map(([key, label, desc]) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', background: '#0f172a', borderRadius: 10 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#e2e8f0' }}>{label}</div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>{desc}</div>
                  </div>
                  <label style={{ position: 'relative', display: 'inline-block', width: 44, height: 24, flexShrink: 0 }}>
                    <input
                      type="checkbox"
                      checked={(features as any)[key]}
                      onChange={e => setFeatures(f => f ? { ...f, [key]: e.target.checked } : f)}
                      style={{ opacity: 0, width: 0, height: 0 }}
                    />
                    <span style={{
                      position: 'absolute', cursor: 'pointer', inset: 0,
                      borderRadius: 24, transition: '0.3s',
                      background: (features as any)[key] ? '#6366f1' : '#334155',
                    }}>
                      <span style={{
                        position: 'absolute', width: 18, height: 18, borderRadius: '50%',
                        background: '#fff', top: 3, transition: '0.3s',
                        left: (features as any)[key] ? 23 : 3,
                      }} />
                    </span>
                  </label>
                </div>
              ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginTop: 20 }}>
              <div>
                <label style={labelStyle}>Whisper Model</label>
                <select
                  value={features.whisper_model}
                  onChange={e => setFeatures(f => f ? { ...f, whisper_model: e.target.value } : f)}
                  style={selectStyle}
                >
                  {WHISPER_MODELS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
                <div style={{ fontSize: 10, color: '#64748b', marginTop: 4 }}>tiny=fastest, medium=most accurate</div>
              </div>

              <div>
                <label style={labelStyle}>LLM Model</label>
                <select
                  value={features.llm_model}
                  onChange={e => setFeatures(f => f ? { ...f, llm_model: e.target.value } : f)}
                  style={selectStyle}
                >
                  {LLM_MODELS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
                <div style={{ fontSize: 10, color: '#64748b', marginTop: 4 }}>For summaries &amp; analysis</div>
              </div>

              <div>
                <label style={labelStyle}>Language</label>
                <select
                  value={features.language}
                  onChange={e => setFeatures(f => f ? { ...f, language: e.target.value } : f)}
                  style={selectStyle}
                >
                  {LANGUAGES.map(l => <option key={l} value={l}>{l === 'auto' ? 'Auto-detect' : l.toUpperCase()}</option>)}
                </select>
                <div style={{ fontSize: 10, color: '#64748b', marginTop: 4 }}>Force language or auto-detect</div>
              </div>
            </div>

            <button onClick={saveFeatures} disabled={saving} style={{
              marginTop: 20, background: '#6366f1', color: '#fff', border: 'none',
              borderRadius: 10, padding: '12px 28px', fontSize: 14, fontWeight: 700, cursor: 'pointer',
              opacity: saving ? 0.6 : 1,
            }}>
              {saving ? '⏳ Saving…' : '💾 Save Features'}
            </button>
          </div>
        </div>
      )}

      {/* PERMISSIONS */}
      {tab === 'permissions' && (
        <div>
          <div style={{ marginBottom: 14, fontSize: 13, color: '#64748b' }}>
            Manage per-user AI feature access. Admin and Tenant Admin roles always have full access.
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #1e293b' }}>
                  {['User', 'Role', 'Use AI', 'View Transcript', 'Download', 'Summary', 'Sentiment', 'Action Items'].map(h => (
                    <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: '#64748b', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.user_id} style={{ borderBottom: '1px solid #1e293b' }}>
                    <td style={{ padding: '10px 12px', color: '#e2e8f0', fontWeight: 600 }}>
                      {u.display_name || u.username}
                    </td>
                    <td style={{ padding: '10px 12px', color: '#64748b' }}>{u.role}</td>
                    {(['can_use_ai', 'can_view_transcript', 'can_download_transcript', 'can_view_summary', 'can_view_sentiment', 'can_view_action_items'] as const).map(field => (
                      <td key={field} style={{ padding: '10px 12px', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={!!u[field]}
                          disabled={savingPerms[u.user_id]}
                          onChange={e => updateUserPerm(u.user_id, field, e.target.checked)}
                          style={{ cursor: 'pointer', width: 16, height: 16, accentColor: '#6366f1' }}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {users.length === 0 && (
              <div style={{ textAlign: 'center', padding: 40, color: '#334155', fontSize: 13 }}>No users found.</div>
            )}
          </div>
        </div>
      )}

      {/* JOBS */}
      {tab === 'jobs' && (
        <div>
          <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ fontSize: 13, color: '#64748b' }}>{jobsTotal} total jobs</div>
            <div style={{ display: 'flex', gap: 6, marginLeft: 'auto', flexWrap: 'wrap' }}>
              {['', 'pending', 'processing', 'completed', 'failed', 'skipped'].map(s => (
                <button key={s} onClick={() => { setJobFilter(s); setJobPage(1); loadJobs(1, s); }}
                  style={{
                    background: jobFilter === s ? '#6366f1' : '#1e293b',
                    border: '1px solid #334155', color: jobFilter === s ? '#fff' : '#94a3b8',
                    borderRadius: 8, padding: '5px 12px', fontSize: 11, cursor: 'pointer', fontWeight: 600,
                  }}>
                  {s || 'All'}
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {jobs.map(j => (
              <div key={j.id} style={{ background: '#1e293b', borderRadius: 10, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ fontWeight: 600, fontSize: 12, padding: '3px 8px', borderRadius: 6,
                  background: STATUS_COLORS[j.status] + '22', color: STATUS_COLORS[j.status] || '#64748b' }}>
                  {j.status}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {j.original_name || j.filename || `Recording #${j.recording_id}`}
                  </div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 3 }}>
                    Job #{j.id} · Attempt {j.attempts} · {new Date(j.created_at).toLocaleString()}
                    {j.processing_time_ms && ` · ${(j.processing_time_ms / 1000).toFixed(1)}s`}
                  </div>
                  {j.error_message && (
                    <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>⚠ {j.error_message}</div>
                  )}
                </div>
                {j.status === 'failed' && (
                  <button onClick={() => reprocess(j.recording_id)} style={{
                    background: '#6366f122', border: '1px solid #6366f1', color: '#a5b4fc',
                    borderRadius: 8, padding: '5px 12px', fontSize: 11, cursor: 'pointer', fontWeight: 600,
                  }}>
                    ↻ Retry
                  </button>
                )}
              </div>
            ))}
            {jobs.length === 0 && (
              <div style={{ textAlign: 'center', padding: 40, color: '#334155' }}>No jobs found.</div>
            )}
          </div>

          {jobsTotal > 20 && (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 20 }}>
              <button onClick={() => { const p = jobPage - 1; setJobPage(p); loadJobs(p, jobFilter); }}
                disabled={jobPage <= 1} style={pagBtn}>◀ Prev</button>
              <span style={{ padding: '6px 12px', color: '#64748b', fontSize: 13 }}>
                {jobPage} / {Math.ceil(jobsTotal / 20)}
              </span>
              <button onClick={() => { const p = jobPage + 1; setJobPage(p); loadJobs(p, jobFilter); }}
                disabled={jobPage >= Math.ceil(jobsTotal / 20)} style={pagBtn}>Next ▶</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const cont: React.CSSProperties = { flex: 1, overflowY: 'auto', padding: '24px 28px', background: '#0f172a', color: '#e2e8f0' };
const card: React.CSSProperties = { background: '#1e293b', borderRadius: 12, padding: '18px 20px' };
const cardHdr: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 };
const labelStyle: React.CSSProperties = { display: 'block', fontSize: 12, color: '#94a3b8', fontWeight: 600, marginBottom: 6 };
const selectStyle: React.CSSProperties = { width: '100%', background: '#1e293b', border: '1px solid #334155', color: '#e2e8f0', borderRadius: 8, padding: '8px 10px', fontSize: 13 };
const pagBtn: React.CSSProperties = { background: '#1e293b', border: '1px solid #334155', color: '#94a3b8', borderRadius: 8, padding: '6px 14px', fontSize: 12, cursor: 'pointer' };
